import { createFeatureSelector, createSelector } from '@ngrx/store';
import { selectRouteParam } from 'src/app/core/router.selectors';
import { accountManagersFeatureKey } from './account-managers.reducer';
import { AccountManagersState } from './state/account-managers.state';
import {
    SubmittalStatusEnum,
    submittalStatusesWithAutoRemoval,
    submittalStatusesWithDraft
} from 'src/app/accountmanagers/submittals/model/enums/submittal-status.enum';
import { AccountManagerDashboardAlertType } from 'src/app/accountmanagers/dashboard/enums/account-manager-dashboard.enum';

export const selectAccountManagersState = createFeatureSelector<AccountManagersState>(accountManagersFeatureKey);

export const selectWocUploadResult = createSelector(selectAccountManagersState, (state) => state?.uploadResult);

export const selectWocUploadError = createSelector(selectAccountManagersState, (state) => state?.error);

export const selectSubmittalReassignmentResult = createSelector(
    selectAccountManagersState,
    (state) => state?.submittalReassignResult
);

export const selectSubmittalReassignError = createSelector(
    selectAccountManagersState,
    (state) => state?.submittalReassignError
);

export const selectJobsLookups = createSelector(selectAccountManagersState, (state) => state?.lookups);

export const selectEmploymentTypes = createSelector(
    selectAccountManagersState,
    (state) => state?.lookups?.employmentTypes
);

export const selectOfferRecruiters = createSelector(selectAccountManagersState, (state) => state?.offerRecruiters);

export const selectJobsForSubmittals = createSelector(selectAccountManagersState, (state) => state?.submittalJobs);

export const selectJobsForSubmittalsById = (submittalId: number) =>
    createSelector(selectJobsForSubmittals, (jobs) => jobs.filter((e) => e.submittalId === submittalId));

export const selectSubmittalRemovalReasons = createSelector(
    selectAccountManagersState,
    (state) => state?.submittalRemovalReasons
);

export const selectBulkRemoveFromLiveListResponse = createSelector(
    selectAccountManagersState,
    (state) => state?.bulkRemoveFromLiveListResult
);

export const selectContractId = createSelector(
    selectAccountManagersState,
    selectRouteParam('contractId'),
    (state, contractId) => Number(contractId)
);

export const selectCandidateId = createSelector(
    selectAccountManagersState,
    selectRouteParam('candidateId'),
    (state, candidateId) => Number(candidateId)
);

export const getWdpPlacementInformation = createSelector(
    selectAccountManagersState,
    (state) => state?.placementInformation
);

export const getCandidateProfileInformation = createSelector(
    selectAccountManagersState,
    (state) => state?.candidateProfileInformation
);

export const getCandidateName = createSelector(
    selectAccountManagersState,
    (state) =>
        `${state?.candidateProfileInformation?.firstName ?? ''} ${state?.candidateProfileInformation?.lastName ?? ''}`
);

export const getWdpTravelDetails = createSelector(selectAccountManagersState, (state) => state?.travelDetails);
export const selectWfdTravelDetailsError = createSelector(
    selectAccountManagersState,
    (state) => state.travelDetails && state.error
);

export const getWdpFlightBookingInformation = createSelector(
    selectAccountManagersState,
    (state) => state?.flightBookingInformation
);
export const selectWfdFlightBookingInformationError = createSelector(
    selectAccountManagersState,
    (state) => state?.flightBookingInformation && state.error
);
export const selectWfdHotelDetails = createSelector(selectAccountManagersState, (state) => state?.hotelDetails);
export const selectWfdHotelDetailsError = createSelector(
    selectAccountManagersState,
    (state) => state.hotelDetails && state.error
);

export const selectWdpTravelStatuses = createSelector(
    selectAccountManagersState,
    (state) => state?.lookupTravelStatuses
);

export const selectWdpTravelPlanTypes = createSelector(
    selectAccountManagersState,
    (state) => state?.lookupTravelPlanTypes
);

export const selectWdpGenderTypes = createSelector(selectAccountManagersState, (state) => state?.lookupGenderTypes);

export const selectWdpStateCodes = createSelector(selectAccountManagersState, (state) => state?.lookupStateCodes);

export const selectNotesState = createSelector(selectAccountManagersState, (state) => state?.worforceNotes);

export const selectNoteLoading = createSelector(selectNotesState, (state) => state.loading);
export const selectNotes = createSelector(selectNotesState, (state) => state.notes);

export const selectJobRecommendations = createSelector(selectAccountManagersState, (state) => ({
    recommendations: state?.jobRecommendations.recommendations,
    numTodos: state?.jobRecommendations.jobRecommendationNumTodos,
    numCompleted: state?.jobRecommendations.jobRecommendationNumCompleted,
    numDismissed: state?.jobRecommendations.jobRecommendationNumDismissed,
    showTodoDivider: state?.jobRecommendations.showTodoDivider,
    showDismissedDivider: state?.jobRecommendations.showDismissedDivider
}));
export const selectNumJobRecommendationTodos = createSelector(
    selectAccountManagersState,
    (state) => state?.jobRecommendations.jobRecommendationNumTodos
);
export const selectShowJobRecommendations = createSelector(
    selectAccountManagersState,
    (state) => state?.jobRecommendations.showJobRecommendations
);
export const selectSubmittalVendorInfo = createSelector(
    selectAccountManagersState,
    (state) => state?.submittalVendorInfo
);
export const selectFacilityPlacement = createSelector(selectAccountManagersState, (state) => state?.facilityPlacement);
export const selectSubmittalStatuses = createSelector(selectAccountManagersState, (state) => state.statuses);
export const selectAMLiveListSubmittalStatusesNoDraft = createSelector(selectAccountManagersState, (state) =>
    state.statuses.filter(
        (status) =>
            !submittalStatusesWithAutoRemoval.includes(status.id) && !submittalStatusesWithDraft.includes(status.id)
    )
);

export const offerRescindedSubStatuses = createSelector(
    selectAccountManagersState,
    (state) => state.statuses.find((t) => t.id === SubmittalStatusEnum.OfferRescinded)?.subStatuses
);

export const selectWfdAdditionalInfo = createSelector(selectAccountManagersState, (state) => state?.additionalInfo);

export const selectWfdAdditionalInfoSuccess = createSelector(
    selectAccountManagersState,
    (state) => state?.additionalInfo
);

export const selectWfdAdditionalInfoError = createSelector(
    selectAccountManagersState,
    (state) => state.additionalInfo && state.error
);

export const selectWfdFacilitySystemAdditionalInfo = createSelector(
    selectAccountManagersState,
    (state) => state?.facilitySystemAdditionalInfo
);

export const selectWfdFacilitySystemAdditionalInfoSuccess = createSelector(
    selectAccountManagersState,
    (state) => state?.facilitySystemAdditionalInfo
);

export const selectWfdFacilitySystemAdditionalInfoError = createSelector(
    selectAccountManagersState,
    (state) => state.facilitySystemAdditionalInfo && state.error
);

export const selectAccountManagerDashboardAlerts = createSelector(
    selectAccountManagersState,
    (state) => state.accountManagerDashboardAlertData
);

export const selectIsHubConnectionActive = createSelector(
    selectAccountManagersState,
    (state) => state?.isHubConnectionActive
);

export const selectAccountManagerDashboardAlertsAndShouldFetch = (
    accountManagerId: number,
    alertType: AccountManagerDashboardAlertType
) =>
    createSelector(
        selectAccountManagersState,
        selectShouldFetchData(accountManagerId, alertType),
        (state, shouldFetch) => {
            return {
                shouldFetch,
                alerts: state.accountManagerDashboardAlertData
            };
        }
    );

export const selectShouldFetchData = (accountManagerId: number, alertType: AccountManagerDashboardAlertType) =>
    createSelector(selectAccountManagersState, (state) => {
        return (
            state.accountManagerDashboardAlertData?.accountManagerId === accountManagerId &&
            (state.accountManagerDashboardAlertData?.alertTypes.includes(alertType) ||
                state.accountManagerDashboardAlertData.alertTypes.includes(AccountManagerDashboardAlertType.AllTiles))
        );
    });
